import React, { type ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { Form, Select } from 'antd';
import type { FieldValues } from 'react-hook-form';
import type { RHFBaseProps, CommonChrome } from './types';
import { DEFAULT_FORM_LAYOUT } from './constants';
import { Flag } from '@elements/Flag';
import type { IconProps } from '@phosphor-icons/react';

export type CountryOption = {
  value: string;
  label: string;
  flag?: string;
  icon?: ReactElement<IconProps>;
};

function renderCountryOptionLabel(opt: CountryOption): React.ReactNode {
  if (opt.flag) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Flag className="m-0" name={opt.flag} />
        {opt.icon && opt.icon}
        <span>{opt.label}</span>
      </span>
    );
  }
  return opt.label;
}

export type ControlledCountrySelectProps<TFieldValues extends FieldValues = FieldValues> =
  RHFBaseProps<TFieldValues> &
    CommonChrome & {
      options: CountryOption[];
    } & Omit<
      React.ComponentProps<typeof Select<string>>,
      'value' | 'onChange' | 'options'
    >;

export function ControlledCountrySelect<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  required,
  disabled,
  help,
  options,
  layout,
  fieldClassName,
  suffixIcon,
  loading,
  ...rest
}: ControlledCountrySelectProps<TFieldValues>) {
  const selectOptions = React.useMemo(
    () =>
      options.map(opt => ({
        value: opt.value,
        label: renderCountryOptionLabel(opt),
        // Keep string label for search — optionFilterProp: 'label' fails when label is JSX
        searchLabel: opt.label,
      })),
    [options],
  );

  const filterOption = React.useCallback(
    (input: string, option?: { searchLabel?: string }) =>
      (option?.searchLabel ?? '').toLowerCase().includes((input ?? '').toLowerCase()),
    [],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => (
        <Form.Item
          label={label}
          required={required}
          validateStatus={fieldState.error ? 'error' : undefined}
          help={fieldState.error?.message ?? help}
          layout={layout ?? DEFAULT_FORM_LAYOUT}
          className={fieldClassName}
        >
          <Select<string>
            {...rest}
            suffixIcon={loading ? undefined : suffixIcon}
            loading={loading}
            options={selectOptions}
            value={value ?? undefined}
            onChange={onChange}
            disabled={loading || disabled}
            style={{ width: rest.style?.width ?? '100%' }}
            filterOption={rest.showSearch ? filterOption : rest.filterOption}
          />
        </Form.Item>
      )}
    />
  );
}
